// ---- Coded model registry ----
// Models that legitimately stay TypeScript: seeded/procedural geometry the JSON
// part format can't express (derived loop counts, RNG variation) — every deco
// build lives here (trees taper by seeded random, rocks are noise blobs, the
// well derives its stones…). Everything is still reachable through the
// uniform `modelBuilder(id)` (index.ts) exactly like a JSON model.
//
// A coded builder gets (prims, seed); deco builds take their DecoBuildCtx
// (the same five prims + the seed folded in), so each is a one-line adapter.

import type { Prims } from "../meshkit.ts";
import type { DecoBuildCtx } from "../deco/define.ts";

import tree from "../deco/tree.ts";
import pine from "../deco/pine.ts";
import rock from "../deco/rock.ts";
import bush from "../deco/bush.ts";
import flower from "../deco/flower.ts";
import mushroom from "../deco/mushroom.ts";
import grass from "../deco/grass.ts";
import fern from "../deco/fern.ts";
import stump from "../deco/stump.ts";
import log from "../deco/log.ts";
import boulder from "../deco/boulder.ts";
import birch from "../deco/birch.ts";
import fence from "../deco/fence.ts";
import well from "../deco/well.ts";
import signpost from "../deco/signpost.ts";
import cattail from "../deco/cattail.ts";
import lilypad from "../deco/lilypad.ts";
import driftwood from "../deco/driftwood.ts";
import range from "../deco/range.ts";
import campfire from "../deco/campfire.ts";
import chest, { chestOpen } from "../deco/chest.ts";
import barrel from "../deco/barrel.ts";
import crate from "../deco/crate.ts";
import lanternPost from "../deco/lantern-post.ts";
import noticeBoard from "../deco/notice-board.ts";
import bench from "../deco/bench.ts";
import hideFrame from "../deco/hide-frame.ts";
import hangingNet from "../deco/hanging-net.ts";
import dryingRack from "../deco/drying-rack.ts";
import laundryLine from "../deco/laundry-line.ts";
import rainBarrel from "../deco/rain-barrel.ts";
import planter from "../deco/planter.ts";
import woodpile from "../deco/woodpile.ts";
import netShed from "../deco/net-shed.ts";

export type CodedBuilder = (p: Prims, seed: number) => void;

function deco(build: (ctx: DecoBuildCtx) => void): CodedBuilder {
  return (p, seed) => build({ ...p, seed });
}

export const CODED_MODELS: Record<string, CodedBuilder> = {
  tree: deco(tree),
  pine: deco(pine),
  rock: deco(rock),
  bush: deco(bush),
  flower: deco(flower),
  mushroom: deco(mushroom),
  grass: deco(grass),
  fern: deco(fern),
  stump: deco(stump),
  log: deco(log),
  boulder: deco(boulder),
  birch: deco(birch),
  fence: deco(fence),
  well: deco(well),
  signpost: deco(signpost),
  cattail: deco(cattail),
  lilypad: deco(lilypad),
  driftwood: deco(driftwood),
  range: deco(range),
  campfire: deco(campfire),
  chest: deco(chest),
  // The opened chest — src/interact.ts swaps a placed chest to this build when
  // the server marks it opened (an "open" interact needs "<model>-open").
  "chest-open": deco(chestOpen),
  // Village landmark props (tutorial character pass — weight 0, never scatter).
  barrel: deco(barrel),
  crate: deco(crate),
  "lantern-post": deco(lanternPost),
  "notice-board": deco(noticeBoard),
  bench: deco(bench),
  "hide-frame": deco(hideFrame),
  "hanging-net": deco(hangingNet),
  "drying-rack": deco(dryingRack),
  "laundry-line": deco(laundryLine),
  "rain-barrel": deco(rainBarrel),
  planter: deco(planter),
  woodpile: deco(woodpile),
  "net-shed": deco(netShed),
};
